import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiChevronRight, FiChevronLeft, FiTarget, FiPlus, FiSettings, FiCalculator, FiStar } = FiIcons;

const GetStartedGuide = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to CreateMyMeal",
      icon: FiTarget,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Create perfectly balanced meals that hit your exact macro targets with our intelligent optimization algorithm.
          </p>
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-semibold text-indigo-800 mb-2">Key Features:</h4>
            <ul className="space-y-1 text-sm text-indigo-700">
              <li>• Intelligent meal optimization within your desired ranges</li>
              <li>• 500,000+ foods from FatSecret database</li>
              <li>• Custom food creation</li>
              <li>• Flexible macro targeting with thresholds</li>
              <li>• Real-time serving size adjustments</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Step 1: Select Your Foods",
      icon: FiPlus,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Choose foods from our extensive database or add your own custom foods.
          </p>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">Pro Tips:</h4>
            <ul className="space-y-1 text-sm text-green-700">
              <li>• Set realistic min/max ranges for each food</li>
              <li>• Include a variety of macronutrients</li>
              <li>• Switch between local database and FatSecret for more options</li>
              <li>• Use the category filter to find foods quickly</li>
            </ul>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Example Serving Ranges:</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Chicken breast: 100-200g</li>
              <li>• Rice: 50-150g</li>
              <li>• Vegetables: 100-300g</li>
              <li>• Oils: 5-15ml</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Step 2: Set Your Targets",
      icon: FiTarget,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Define your calorie and macro targets with flexible thresholds.
          </p>
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-orange-800 mb-2">Two Modes Available:</h4>
            <div className="space-y-2 text-sm text-orange-700">
              <div>
                <strong>Simple Mode:</strong> Only calories are optimized
              </div>
              <div>
                <strong>Advanced Mode:</strong> All macros are optimized within thresholds
              </div>
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-800 mb-2">Threshold Guidelines:</h4>
            <ul className="space-y-1 text-sm text-purple-700">
              <li>• 5-10%: Very strict targeting</li>
              <li>• 10-15%: Balanced approach (recommended)</li>
              <li>• 15-20%: More flexible targeting</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Step 3: Calculate & Optimize",
      icon: FiCalculator,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Our algorithm will find the perfect serving sizes within your specified ranges.
          </p>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">How It Works:</h4>
            <ul className="space-y-1 text-sm text-green-700">
              <li>• Tests thousands of combinations</li>
              <li>• Respects your min/max serving ranges</li>
              <li>• Optimizes for your macro targets</li>
              <li>• Provides the best possible solution</li>
            </ul>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">After Calculation:</h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Edit serving sizes manually if needed</li>
              <li>• See real-time macro updates</li>
              <li>• Get recommendations for improvements</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Advanced Tips & Tricks",
      icon: FiStar,
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Master these techniques to get the most out of the meal calculator.
          </p>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-800 mb-2">Optimization Tips:</h4>
            <ul className="space-y-1 text-sm text-yellow-700">
              <li>• Include foods from different macro categories</li>
              <li>• Use wider ranges for more flexibility</li>
              <li>• Start with 10% thresholds and adjust as needed</li>
              <li>• Add high-protein foods to hit protein targets</li>
            </ul>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-semibold text-red-800 mb-2">Troubleshooting:</h4>
            <ul className="space-y-1 text-sm text-red-700">
              <li>• If no solution found, increase thresholds</li>
              <li>• Add or remove foods to balance macros</li>
              <li>• Adjust serving ranges for more flexibility</li>
              <li>• Check the recommendations in results</li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <SafeIcon icon={steps[currentStep].icon} className="text-2xl text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              {steps[currentStep].title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {steps[currentStep].content}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="p-6 border-t border-gray-200">
          {/* Progress Indicator */}
          <div className="flex justify-center mb-4">
            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index === currentStep ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} />
              <span>Previous</span>
            </button>

            <span className="text-sm text-gray-500">
              {currentStep + 1} of {steps.length}
            </span>

            {currentStep === steps.length - 1 ? (
              <button
                onClick={onClose}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                <span>Get Started</span>
                <SafeIcon icon={FiTarget} />
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <span>Next</span>
                <SafeIcon icon={FiChevronRight} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GetStartedGuide;